import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController,
  Legend,
  Tooltip,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { Box } from "@mui/material";
import { useEffect, useRef, useMemo, useState } from "react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  LineController,
  Legend,
  Tooltip
);

export default function NewDoctorCountGraph({ lineBarChartData }) {
  const chartRef = useRef(null);
  const dataRef = useRef(null);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isSmallScreen = screenWidth < 900;

  const customPointsPlugin = {
    id: "customPointsPlugin",
    afterDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      const xScale = scales.x;
      const bottom = chartArea.bottom;
      const offsets = [40, 80];

      customDatasets.forEach((dataset, datasetIndex) => {
        const pointStyle = dataset.pointStyle || "circle";
        const pointRadius = dataset.pointRadius || (isSmallScreen ? 4 : 6);
        const color = dataset.backgroundColor;

        dataset.data.forEach((value, index) => {
          const x = xScale.getPixelForValue(index);
          const y = bottom + offsets[datasetIndex];

          ctx.save();
          ctx.beginPath();
          ctx.fillStyle = color;
          if (pointStyle === "circle") {
            ctx.arc(x, y, pointRadius, 0, Math.PI * 2);
            ctx.fill();
          } else if (pointStyle === "rectRot") {
            ctx.translate(x, y);
            ctx.rotate(Math.PI / 4);
            ctx.fillRect(
              -pointRadius,
              -pointRadius,
              pointRadius * 2,
              pointRadius * 2
            );
            ctx.resetTransform();
          }
          ctx.restore();

          ctx.save();
          ctx.font = `${isSmallScreen ? "10px" : "12px"} Arial`;
          ctx.fillStyle = "#000";
          ctx.textAlign = "center";
          const displayValue =
            Number.isFinite(value) && value !== 0 ? value.toString() : "0";
          ctx.fillText(displayValue, x, y + pointRadius + 10);
          ctx.restore();
        });
      });
    },
  };

  const dataLabelsPlugin = {
    id: "dataLabelsPlugin",
    afterDatasetsDraw(chart) {
      const { ctx, scales } = chart;
      const yScale = scales.y;
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        if (
          dataset.type === "bar" ||
          dataset.label === "Frequency Compliance A+B %" ||
          dataset.label === "Unique Coverage A+B %"
        ) {
          const meta = chart.getDatasetMeta(datasetIndex);
          dataset.data.forEach((value, index) => {
            if (value != null) {
              const element = meta.data[index];
              if (
                element &&
                element.x !== undefined &&
                element.y !== undefined
              ) {
                const x = element.x;
                const y = yScale.getPixelForValue(value) - 4;
                const text =
                     dataset.label === "Frequency Compliance A+B %" ||
          dataset.label === "Unique Coverage A+B %"
                    ? `${
                        Number.isFinite(dataset.originalData[index]) &&
                        dataset.originalData[index] !== 0
                          ? dataset.originalData[index].toFixed(0)
                          : "0"
                      }%`
                    : Number.isFinite(value) && value !== 0
                    ? value.toString()
                    : "0";
                ctx.save();
                ctx.font = `${isSmallScreen ? "8px" : "12px"} Arial`;
                ctx.fillStyle = "#000";
                ctx.textAlign = "center";
                ctx.textBaseline = "bottom";
                ctx.fillText(text, x, y);
                ctx.restore();
              }
            }
          });
        }
      });
    },
  };
  const customTooltipPlugin = {
    id: "customTooltipPlugin",
    beforeTooltipDraw(chart) {
      const tooltip = chart.tooltip;
      if (tooltip && tooltip.dataPoints) {
        const activePoint = tooltip.dataPoints[0];
        if (activePoint) {
          const index = activePoint.dataIndex;
          customDatasets.forEach((dataset) => {
            tooltip.dataPoints.push({
              dataset: {
                label: dataset.label,
                data: dataset.data,
              },
              parsed: dataset.data[index] ?? 0,
              dataIndex: index,
              datasetIndex: -1,
            });
          });

          tooltip.dataPoints = tooltip.dataPoints.map((point) => {
            if (
              point.dataset.label === "Frequency Compliance A+B %" ||
              point.dataset.label === "Unique Coverage A+B %"
            ) {
              return {
                ...point,
                parsed: point.dataset.originalData?.[point.dataIndex] ?? 0,
              };
            }
            return point;
          });
        }
      }
    },
  };

  useEffect(() => {
    ChartJS.register(customPointsPlugin, dataLabelsPlugin, customTooltipPlugin);
    return () => {
      ChartJS.unregister(
        customPointsPlugin,
        dataLabelsPlugin,
        customTooltipPlugin
      );
    };
  }, [isSmallScreen]);
  const data = useMemo(() => {
    if (
      !lineBarChartData ||
      !lineBarChartData.datasets ||
      !lineBarChartData.months ||
      lineBarChartData.months.length === 0
    ) {
      return null;
    }

    const assocDoctorData = (
      lineBarChartData.datasets.find(
        (ds) => ds.label === "Associated Doctor Count"
      )?.data || []
    ).map((value) => (value == null || isNaN(value) ? 0 : value));

    return {
      labels: lineBarChartData.months,
      datasets: lineBarChartData.datasets.map((dataset) => {
        const sanitizedData = (dataset.data || []).map((value) =>
          value == null || isNaN(value) ? 0 : value
        );

        switch (dataset?.label) {
          case "Associated Doctor Count":
            return {
              type: "bar",
              label: dataset.label,
              data: sanitizedData,
              backgroundColor: "#20204A",
              yAxisID: "y",
              barPercentage: isSmallScreen ? 0.33 : 0.35,
              categoryPercentage: isSmallScreen ? 0.33 : 0.35,
              order: 1,
            };
          case "Visits":
            return {
              type: "bar",
              label: dataset.label,
              data: sanitizedData,
              backgroundColor: "#FF4FB5",
              yAxisID: "y",
              barPercentage: isSmallScreen ? 0.33 : 0.35,
              categoryPercentage: isSmallScreen ? 0.33 : 0.35,
              order: 1,
            };
          case "Frequency Compliance A+B %":
            return {
              type: "line",
              label: dataset.label,
              data: sanitizedData.map((val, index) =>
                val === 0
                  ? 0
                  : (Math.min(val, 100) / 100) * assocDoctorData[index]
              ),
              originalData: sanitizedData,
              borderColor: "#5BB5FF",
              backgroundColor: "#5BB5FF",
              yAxisID: "y",
              tension: 0,
              pointRadius: isSmallScreen ? 3 : 5,
              pointHoverRadius: isSmallScreen ? 5 : 7,
              order: 2,
            };
          case "Unique Coverage A+B %":
            return {
              type: "line",
              label: dataset.label,
              data: sanitizedData.map((val, index) =>
                val === 0
                  ? 0
                  : (Math.min(val, 100) / 100) * assocDoctorData[index]
              ),
              originalData: sanitizedData,
              borderColor: "#005172",
              backgroundColor: "#005172",
              yAxisID: "y",
              tension: 0,
              pointRadius: isSmallScreen ? 3 : 5,
              pointHoverRadius: isSmallScreen ? 5 : 7,
              order: 2,
            };
          case "Call Average":
            return {
              type: "line",
              label: dataset.label,
              data: sanitizedData,
              borderColor: "green",
              backgroundColor: "green",
              showLine: false,
              pointRadius: 0,
              order: 3,
            };
          case "Working Days":
            return {
              type: "line",
              label: dataset.label,
              data: sanitizedData,
              borderColor: "red",
              backgroundColor: "red",
              showLine: false,
              pointRadius: 0,
              order: 3,
            };
          default:
            return {
              ...dataset,
              data: sanitizedData,
            };
        }
      }),
    };
  }, [lineBarChartData, isSmallScreen]);
  const customDatasets = useMemo(
    () => [
      {
        label: "Call Average",
        data: (
          lineBarChartData?.datasets?.find((ds) => ds.label === "Call Average")
            ?.data || []
        ).map((value) => (value == null || isNaN(value) ? 0 : value)),
        backgroundColor: "green",
        pointStyle: "circle",
        pointRadius: isSmallScreen ? 4 : 6,
      },
      {
        label: "Working Days",
        data: (
          lineBarChartData?.datasets?.find((ds) => ds.label === "Working Days")
            ?.data || []
        ).map((value) => (value == null || isNaN(value) ? 0 : value)),
        backgroundColor: "red",
        pointStyle: "rectRot",
        pointRadius: isSmallScreen ? 4 : 6,
      },
    ],
    [lineBarChartData, isSmallScreen]
  );

  const maxBarValue = useMemo(() => {
    if (!lineBarChartData || !lineBarChartData.datasets) return 0;
    const barDatasets = lineBarChartData.datasets.filter(
      (ds) => ds.label === "Associated Doctor Count" || ds.label === "Visits"
    );
    const lineDatasets = lineBarChartData.datasets.filter(
      (ds) =>
        ds.label === "Frequency Compliance A+B %" ||
        ds.label === "Unique Coverage A+B %"
    );
    const assocDoctorData = (
      lineBarChartData.datasets.find(
        (ds) => ds.label === "Associated Doctor Count"
      )?.data || []
    ).map((value) => (value == null || isNaN(value) ? 0 : value));

    const scaledLineValues = lineDatasets.flatMap((ds, dsIndex) =>
      (ds.data || []).map((val, index) =>
        val === 0 ? 0 : (Math.min(val, 100) / 100) * assocDoctorData[index]
      )
    );

    return Math.max(
      ...barDatasets.flatMap((ds) =>
        (ds.data || []).filter((val) => val != null && !isNaN(val))
      ),
      ...scaledLineValues,
      0
    );
  }, [lineBarChartData]);

  const maxAssociatedDoctorCount = useMemo(() => {
    if (!lineBarChartData || !lineBarChartData.datasets) return 0;
    const assocDoctorDataset = lineBarChartData.datasets.find(
      (ds) => ds.label === "Associated Doctor Count"
    );
    return assocDoctorDataset
      ? Math.max(
          ...(assocDoctorDataset.data || []).filter(
            (val) => val != null && !isNaN(val)
          ),
          0
        )
      : 0;
  }, [lineBarChartData]);
  const options = useMemo(
    () => ({
      responsive: true,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          position: "top",
          align: "end",
          labels: {
            usePointStyle: true,
            pointStyle: "rect",
            boxWidth: isSmallScreen ? 6 : 8,
            boxHeight: isSmallScreen ? 6 : 8,
            padding: 20,
            font: {
              size: isSmallScreen ? 10 : 14,
            },
            generateLabels: (chart) => {
              const datasets = chart.data.datasets;
              return datasets.map((dataset, i) => {
                const meta = chart.getDatasetMeta(i);
                const hidden = meta.hidden || dataset.hidden;

                let pointStyle = "rect";
                let boxWidth = isSmallScreen ? 6 : 8;
                let boxHeight = isSmallScreen ? 6 : 8;
                let lineWidth = 1;

                if (dataset.label === "Call Average") {
                  pointStyle = "circle";
                  boxWidth = isSmallScreen ? 6 : 8;
                  boxHeight = isSmallScreen ? 6 : 8;
                } else if (
                  dataset.label === "Frequency Compliance A+B %" ||
                  dataset.label === "Unique Coverage A+B %"
                ) {
                  pointStyle = "line";
                  boxWidth = isSmallScreen ? 3 : 4;
                  boxHeight = isSmallScreen ? 10 : 12;
                  lineWidth = isSmallScreen ? 1.5 : 2;
                } else if (
                  dataset.label === "Associated Doctor Count" ||
                  dataset.label === "Visits"
                ) {
                  pointStyle = "rect";
                  boxWidth = isSmallScreen ? 6 : 8;
                  boxHeight = isSmallScreen ? 6 : 8;
                } else if (dataset.label === "Working Days") {
                  pointStyle = "rectRot";
                  boxWidth = isSmallScreen ? 6 : 8;
                  boxHeight = isSmallScreen ? 6 : 8;
                }

                return {
                  text: dataset.label || "",
                  fillStyle: dataset.backgroundColor,
                  strokeStyle: dataset.borderColor,
                  lineWidth: lineWidth,
                  pointStyle: pointStyle,
                  boxWidth: boxWidth,
                  boxHeight: boxHeight,
                  hidden: hidden,
                  datasetIndex: i,
                };
              });
            },
          },
        },
        tooltip: {
          enabled: true,
          bodyFont: {
            size: isSmallScreen ? 10 : 12,
          },
          callbacks: {
            label: function (context) {
              const dataset = context.dataset;
              const index = context.dataIndex;
              const value =
                dataset.label === "Frequency Compliance A+B %" ||
                dataset.label === "Unique Coverage A+B %"
                  ? dataset.originalData?.[index] ?? 0
                  : dataset.data[index] ?? 0;
              return dataset.label === "Frequency Compliance A+B %" ||
                dataset.label === "Unique Coverage A+B %"
                ? `${dataset.label}: ${value}%`
                : `${dataset.label}: ${value}`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: false,
          grid: {
            display: false,
            drawOnChartArea: false,
            drawTicks: false,
          },
          border: {
            display: true,
            color: "#000",
          },
          ticks: {
            display: true,
            font: {
              size: isSmallScreen ? 10 : 12,
            },
          },
        },
        y: {
          type: "linear",
          position: "left",
          stacked: false,
          beginAtZero: true,
          max: maxBarValue * 1.1 || 10,
          grid: {
            display: false,
            drawOnChartArea: false,
            drawTicks: false,
          },
          ticks: {
            display: false,
          },
          border: {
            display: false,
          },
        },
      },
      layout: {
        padding: {
          bottom: 100,
        },
      },
    }),
    [maxBarValue, isSmallScreen]
  );

  useEffect(() => {
    if (data) {
      dataRef.current = data;
    }
  }, [data]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, []);

  if (!data) {
    return <Box>No data available</Box>;
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1200,
        margin: "auto",
        height: "400px !important",
        minHeight: "400px",
        position: "relative",
        "& canvas": {
          maxWidth: "100%",
          maxHeight: "100%",
          width: "100% !important",
          height: "100% !important",
        },
      }}
    >
      <Chart
        key={JSON.stringify(lineBarChartData)}
        ref={chartRef}
        data={data}
        options={options}
        type="bar"
        plugins={[customPointsPlugin, dataLabelsPlugin, customTooltipPlugin]}
      />
    </Box>
  );
}
